<template>
  <div>
    <spinner v-if="busy" />
    <template v-else>
      <div class="row">
        <div v-if="state === 'upload'" class="col-12">
          <div class="upload border rounded mt-3">
            <div class="upload__box d-flex align-items-center flex-column justify-content-center">
              <div class="d-flex flex-column align-items-center pt-3 pb-3 position-relative">
                <input
                  ref="fileInput" type="file"
                  class="upload__box__input"
                  accept="image/jpeg,image/png"
                  @change="filesChange($event.target.name, $event.target.files);"
                >
                <span class="upload__box__icon">
                  <font-awesome-icon :icon="'cloud-upload-alt'" />
                </span>
                <span v-if="isMobile">
                  {{ $t('Tap here to select a file to upload') }}
                </span>
                <span v-else>
                  {{ $t('Click here to select a file or drag and drop a file here to upload') }}
                </span>
                <div v-if="type === 'listingImage'" class="align-items-center d-flex w-100 mt-5">
                  <b-form-checkbox
                    id="optIn"
                    v-model="optIn"
                    class="float-left"
                    name="optIn"
                  />
                  <span class="ml-2 float-left small">{{ $t('allowUsToUseYourPhotos') }}</span>
                </div>
              </div>
            </div>
            <b-alert variant="danger" :show="error !== null">
              {{ error }}
            </b-alert>
            <legend class="float-right small font-weight-500 my-3 text-right text-danger">
              {{ type === 'logo' ? $t('This must be a company logo for which you hold copyright') : $t('Only upload photos for which you hold copyright') }}
            </legend>
          </div>
        </div>
        <template v-else>
          <div class="col-12 col-xl-6">
            <div v-if="state === 'crop'">
              <vue-cropper
                ref="cropper"
                :src="fileUrl(file)"
                alt="Source Image"
              />
            </div>
            <div v-if="state === 'display'" class="w-100 h-100">
              <img :src="listingImageUrl(listingImage.image)" class="img-responsive preview-image">
            </div>
          </div>
          <div class="col-12 col-xl-6">
            <div class="col h-100 align-items-center d-flex flex-column justify-content-between">
              <div v-if="type === 'listingImage'" class="w-100">
                <b-form-group label="Title">
                  <b-form-input v-model="title" type="text" />
                </b-form-group>
                <b-form-group label="Description">
                  <b-form-textarea v-model="description" type="text" />
                </b-form-group>
              </div>
              <div v-if="state === 'display'" class="d-flex w-100 justify-content-left my-2">
                <b-button
                  variant="outline-secondary"
                  title="Edit image"
                  size="sm"
                  class="mr-5"
                  @click.prevent="state = 'crop'"
                >
                  <font-awesome-icon :icon="'edit'" />
                  <span class="ml-1">{{ $t('Edit image') }}</span>
                </b-button>
              </div>
              <div v-else class="d-flex w-100 justify-content-left my-2">
                <b-button
                  variant="outline-secondary"
                  title="Rotate -90deg"
                  size="sm"
                  class="mr-5"
                  @click.prevent="rotate(-90)"
                >
                  <font-awesome-icon :icon="'undo-alt'" />
                  <span class="ml-1">{{ $t('Rotate left') }}</span>
                </b-button>
                <b-button
                  variant="outline-secondary"
                  title="Rotate 90deg"
                  size="sm"
                  @click.prevent="rotate(90)"
                >
                  <font-awesome-icon :icon="'redo-alt'" />
                  <span class="ml-1">{{ $t('Rotate right') }}</span>
                </b-button>
              </div>
              <div class="d-flex w-100 justify-content-left mt-2">
                <b-button size="sm" variant="outline-secondary" @click="clear">
                  {{ $t('Cancel') }}
                </b-button>
                <b-button size="sm" class="ml-5" variant="primary" @click="addCroppedFile">
                  {{ type === 'listingImage' ? (listingImage ? $t('Update') : $t('Add Image')) : $t('Upload') }}
                </b-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import UserDevice from "@/imports/mixins/userDevice";
import Spinner from "@/components/animation/Spinner";

const CANVAS_WIDTH = 468;
const IMAGE_CACHE_BASE = process.env.VUE_APP_IMAGE_CACHE_URL;
const LISTING_IMAGE_FILTER = 'listing_work_original/images/installer/work/'

export default {
  name: "CustomFileUpload",
  components: {Spinner, VueCropper},
  mixins: [UserDevice],
  props: ["type", "maxSize", "listingImage"],
  data: function() {
    return {
      file: null,
      error: null,
      optIn: true,
      mime: null,
      busy: false,
      state: 'upload',
      title: '',
      description: '',
      saveEndpoint: null,
    }
  },
  computed: {
    listing: function() {
      return this.$store.state.company.MAIN_LISTING;
    }
  },
  methods: {
    filesChange: function(name, files) {
      this.error = null;
      this.mime = files[0].type;
      const fileSize = ((files[0].size/1024)/1024);
      const maxSize = (this.maxSize/1024/1024).toFixed(0);
      if (fileSize > maxSize) {
        this.error = "File is too large, a maximum of "+maxSize+" MB file size is allowed.";
        return;
      }
      this.file = files[0];
      this.state = "crop";
      this.$emit('state', false);
    },
    fileUrl(file) {
      return (this.listingImage) ? this.listingImageUrl(file) : URL.createObjectURL(file)
    },
    saveImage: function(image) {
      this.saveEndpoint = this.type === 'logo' ? 'saveListingLogo' : 'saveListingImages';
      let formData = new FormData();
      formData.append('optIn', this.optIn);
      formData.append('title', this.title);
      formData.append('description', this.description);
      this.addImageFromCanvas(image, formData);
    },
    addImageFromCanvas: function(image, formData) {
      image.canvas.toBlob(blob => {
        formData.append(this.type, blob, image.name);
        this.postImage(formData);
      }, image.mime)
    },
    updateImage: function(image) {
      this.saveEndpoint = 'updateListingImage';
      let formData = new FormData();
      formData.append('title', this.title);
      formData.append('description', this.description);
      formData.append('id', this.listingImage.id)
      if (image) {
        this.addImageFromCanvas(image, formData)
      } else {
        this.postImage(formData)
      }
    },
    addCroppedFile: function() {
      if (this.listingImage) {
        let image = null;
        if (this.state === "crop") {
          image = {'canvas': this.$refs.cropper.getCroppedCanvas(), 'name': this.file.name, 'mime': this.mime};
        }
        this.updateImage(image);
      } else {
        const image = {'canvas': this.$refs.cropper.getCroppedCanvas(), 'name': this.file.name, 'mime': this.mime};
        this.saveImage(image);
      }
    },
    postImage: function(formData) {
      this.busy = true;
      this.$store.dispatch(this.saveEndpoint, {listing: this.listing, fd: formData})
        .finally(() => {
          this.clear();
          this.busy = false;
        })
    },
    rotate(deg) {
      this.$refs.cropper.rotate(deg);
      const data = this.$refs.cropper.getImageData();
      const rs = (data.rotate === 90 || data.rotate === 270) ? (1 - (data.top/data.height * 2)) : (CANVAS_WIDTH / data.naturalWidth);
      this.$refs.cropper.zoomTo(rs);
    },
    listingImageUrl: function(path) {
      return IMAGE_CACHE_BASE + LISTING_IMAGE_FILTER + path;
    },
    clear: function() {
      this.file=null;
      this.state='upload';
      this.title=null;
      this.description=null;
      this.$emit('cancel');
      this.$emit('state', true);
    }
  },
  watch: {
    listingImage: function(value) {
      if (value !== null) {
        this.file = value.image;
        this.state = 'display';
        this.title = value.alt;
        this.description = value.description;
      } else {
        this.file = null;
        this.state = "upload";
      }
    }
  }
}
</script>

<style scoped lang="scss">
.upload {
  &__box {
    width: 100%;
    padding: 10px;
    cursor: pointer;
    transition: $transition-base;

    &__icon {
      font-size: 24px;
    }

    &__input {
      opacity: 0;
      top: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      cursor: pointer;
    }

    &:hover {
      color: $white;
      background-color: $secondary;
    }
  }
}

.image-wrapper {
  position: relative;

  .delete-icon {
    color: #000000;
    margin-top: -12px;
    margin-right: -12px;
    background-color: #ffffff;
    font-size: 20px;
    cursor: pointer;
    border-radius: 50%;
    height: 20px;
    width: 20px;
    line-height: 1;
    overflow: hidden;
    .svg-inline-fa {
      height: 20px !important;
      width: 20px !important;
    }
  }
}

.preview-image{
  max-width: 100%;
  max-height: 100%;
}

.cropped-preview {
  width: 100%;
  cursor: pointer;
  max-width: 150px;
  max-height: 105px;
}

::v-deep {
  .modal-header {
    .close {
      padding: 1px;
      line-height: 1;
    }
  }
}
</style>
